import { css } from '@emotion/react';
import { TextBody2RRMixin } from '@mobalytics/shared';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { media } from '../../../../../ui/global/media-query';
import { PlanT } from '../../../payment.types';
import { PlanExtraFeature, TreasuryPaymentsProduct } from '../../../../../__generated/treasury/types';
import { ProductAbbreviation } from '../../../../../types/product';
import { LOL_SEASON_PASS_SKIN_OFFER_COUPON, TFT_SET18_OFFER_COUPON } from '../../../../../constants';
import { usePaymentFilters } from '../../../hooks/payment-filters.hook';
import { DynamicModuleSlug, useDynamicModules } from '../../../../../components/dynamic-modules';

// Every game from common game-config is listed in the Ad-free plan except these game-config ids.
const AD_FREE_EXCLUDED_GAMES = ['lol', 'tft'];

const joinGameNames = (names: string[]): string =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');

interface Props {
  className?: string;
  plan: PlanT;
}

export const Benefits: React.FC<Props> = (props) => {
  const { className, plan } = props;
  const { t } = useTranslation();
  const { productAbbreviation } = usePaymentFilters();
  const availableGames = useDynamicModules()[DynamicModuleSlug.GAME_CONFIG]?.availableGames;

  const adFreeGamesText = React.useMemo(() => {
    const names = availableGames
      ?.filter((availableGame) => !AD_FREE_EXCLUDED_GAMES.includes(availableGame.id))
      .map((availableGame) => availableGame.title);
    return names?.length ? `Remove ads for ${joinGameNames(names)}` : null;
  }, [availableGames]);

  const hasTftEgg = plan.planFeatures?.includes(PlanExtraFeature.EGG_SKIN);
  if (hasTftEgg)
    return (
      <ul css={wrapperCss} className={className}>
        <li>{t('Ad-free experience')}</li>
        <li>{t('All current and future Mobalytics Plus features')}</li>
        <li>{t('Immediately get a free TFT little legend')}</li>
        <li>{t('The first 4 months are non refundable')}</li>
      </ul>
    );

  const hasLolSkin = plan.planFeatures?.includes(PlanExtraFeature.SKIN_CODE);
  if (hasLolSkin) {
    return (
      <ul css={wrapperCss} className={className}>
        <li>{t('Ad-free experience')}</li>
        <li>{t('All current and future Mobalytics Plus features')}</li>
        <li>{t('Immediately get a free LOL skin')}</li>
        <li>{t('The first 4 months are non refundable')}</li>
      </ul>
    );
  }

  if (plan.planType === TreasuryPaymentsProduct.AD_FREE) {
    return (
      <ul css={wrapperCss} className={className}>
        {adFreeGamesText && <li>{adFreeGamesText}</li>}
        <li>{t('See exclusive sneak peeks')}</li>
        <li>{t('Get Ad-slayer Discord role')}</li>
        <li>{t('Support the Mobalytics team <3')}</li>
      </ul>
    );
  }

  if (plan.appliedCoupon === LOL_SEASON_PASS_SKIN_OFFER_COUPON) {
    return (
      <ul css={wrapperCss} className={className}>
        <li>{t('Ad-free experience')}</li>
        <li>{t('All current and future Mobalytics Plus features')}</li>
        <li>{t('Non refundable')}</li>
      </ul>
    );
  }

  if (plan.appliedCoupon === TFT_SET18_OFFER_COUPON) {
    return (
      <ul css={wrapperCss} className={className}>
        <li>{t('Ad-free experience')}</li>
        <li>{t('All current and future Mobalytics Plus features')}</li>
        <li>{t('Immediately get a free TFT little legend')}</li>
        <li>{t('The first 12 months are non refundable')}</li>
      </ul>
    );
  }

  if (productAbbreviation === ProductAbbreviation.POE_2) {
    return (
      <ul css={wrapperCss} className={className}>
        <li>{t('Build Tracker')}</li>
        <li>{t('Ad-free experience')}</li>
        <li>{t('All current and future Mobalytics Plus features')}</li>
      </ul>
    );
  }

  return (
    <ul css={wrapperCss} className={className}>
      <li>{t('Ad-free experience')}</li>
      <li>{t('All current and future Mobalytics Plus features')}</li>
    </ul>
  );
};

const wrapperCss = css`
  ${TextBody2RRMixin};
  color: var(--general-grey-100);
  padding-left: 0;
  ${media.medium} {
    display: none;
  }
  & > li {
    display: flex;
    margin-bottom: 8px;
    list-style-type: none;

    &:before {
      content: '•';
      font-size: 12px;
      color: var(--general-grey-300);
      margin-right: 8px;
      vertical-align: middle;
    }
  }
`;
